import React from 'react';
import { safePad } from '../../utils/safeArea';
import { RING } from '../wall/wallTokens';

/*
 * Sign-up v3 chrome — design_handoff_signup_v3 (Oct 22 2026; the cards, pills and footer are
 * Onboarding v2's, kept). The FIELDS come from the shared Portal field kit
 * (`components/profile/fields`), which the Portal Editor renders too. What lives here is only
 * the page around them: the top bar with the four segments, the section card, its head, the 50px
 * pill input, the iridescent ring and the sticky footer.
 */

export const PINK = '#FF66C4';   // error / "still needed"
export const CYAN = '#22ECFB';   // available, cover, live

/** A step's card: #111, the #1F1F1F inset ring, radius 28 (22 on a phone). */
export function SectionCard({ phone, gap = 16, ring = '#1F1F1F', children, style, ...rest }) {
  return (
    <section
      style={{
        display: 'flex', flexDirection: 'column', gap, minWidth: 0,
        padding: phone ? 18 : '22px 24px', borderRadius: phone ? 22 : 28,
        background: '#111', boxShadow: `inset 0 0 0 1px ${ring}`, boxSizing: 'border-box',
        transition: 'box-shadow .2s', ...style,
      }}
      {...rest}
    >
      {children}
    </section>
  );
}

/** The card's title row: 11px/800 caps on the left, a count or controls on the right. */
export function SectionHead({ title, note, right }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>
      <span style={{ minWidth: 0 }}>{title}{note && <span style={{ color: '#6B6B6B' }}> · {note}</span>}</span>
      {right}
    </span>
  );
}

export const HELP = { fontSize: 12, lineHeight: 1.55, color: '#8A8A8A' };
export const LABEL = { fontSize: 10, fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase' };

/** A 50px pill input on #000 — the one field shape every step uses. */
export function PillField({ label, note, children, hint }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <span style={LABEL}>{label}{note && <span style={{ color: '#6B6B6B' }}> · {note}</span>}</span>
      {children}
      {hint && <span style={{ fontSize: 11, lineHeight: 1.5, color: '#6B6B6B' }}>{hint}</span>}
    </label>
  );
}

export const pill = (ring = '#2A2A2A') => ({
  height: 50, padding: '0 20px', border: 'none', borderRadius: 999, background: '#000',
  boxShadow: `inset 0 0 0 1px ${ring}`, fontFamily: 'inherit', fontSize: 14, color: '#fff',
  outline: 'none', minWidth: 0, width: '100%', boxSizing: 'border-box',
});

/** A pill with a fixed `@`-style prefix. */
export function PrefixPill({ prefix, ring, children }) {
  return (
    <span className="pe-in-wrap" style={{ ...pill(ring), display: 'flex', alignItems: 'center', padding: '0 20px' }}>
      <span style={{ fontSize: 14, color: '#6B6B6B', whiteSpace: 'nowrap' }}>{prefix}</span>
      {children}
    </span>
  );
}

/** A bare input that fills a PrefixPill. */
export const bareInput = (color = '#fff') => ({
  flex: 1, minWidth: 0, height: '100%', border: 'none', background: 'none', boxShadow: 'none',
  outline: 'none', fontFamily: 'inherit', fontSize: 14, color,
});

/** 34px pills in the card heads (VIEW · + ADD PHOTOS · COPY · the gap jumps). */
// `style` is destructured and merged rather than left in `rest`, which would land after this
// button's own `style` and replace it wholesale — see the note on `PrefixInput` in the field kit.
export function MiniPill({ bg = '#1F1F1F', fg = '#fff', height = 34, hot = false, children, style, ...rest }) {
  return (
    <button type="button" className={`pk-btn ${hot ? 'pk-primary-on-black' : 'pk-secondary-on-black'}`} style={{ flexShrink: 0, height, padding: '0 14px', background: bg, color: fg, fontSize: 10, fontWeight: 800, letterSpacing: '.14em', ...style }} {...rest}>
      {children}
    </button>
  );
}

/**
 * The footer: the blocker line, the 56px primary, an optional helper line under it, and the
 * skip text button. Sticky at the bottom over a 22px fade to black, so the primary is always in
 * reach however long the step is.
 *
 * `primaryOff` greys the primary but never disables it, and deliberately does not say
 * `aria-disabled` either. Pressing it while GO LIVE is incomplete is how you LEARN what is still
 * missing — the press writes the "Still needed: …" line and turns the offending rings pink. A
 * button that announces itself as disabled is one a keyboard or screen-reader user can never get
 * that sentence out of, and a greyed-out dead end is the oldest way to lose someone in a form.
 */
export function StepFooter({ blockers, primaryLabel, onPrimary, primaryOff, saving, helper, onSkip, skipLabel = 'SKIP FOR NOW →', desk = false }) {
  const textAlign = desk ? 'left' : 'center';
  // On desktop this sits at the foot of the sticky left column, so it has nothing to float over.
  const shell = desk
    ? { marginTop: 'auto' }
    : { position: 'sticky', bottom: 0, zIndex: 20, marginTop: 'auto', padding: safePad('14px 0 18px'), background: 'linear-gradient(transparent, #000 22px)' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, ...shell }}>
      {blockers && <span role="alert" id="onboard-blockers" data-testid="onboard-blockers" style={{ fontSize: 11, lineHeight: 1.5, color: PINK, textAlign }}>{blockers}</span>}
      <button
        type="button"
        onClick={onPrimary}
        disabled={saving}
        aria-describedby={blockers ? 'onboard-blockers' : undefined}
        data-incomplete={primaryOff ? 'true' : undefined}
        data-testid="onboard-primary"
        className={`pk-btn ${primaryOff || saving ? '' : 'pk-primary-on-black'}`}
        style={{ width: '100%', height: 56, background: primaryOff ? '#2A2A2A' : '#fff', color: primaryOff ? '#8A8A8A' : '#000', fontSize: 12, fontWeight: 800, letterSpacing: '.16em', opacity: 1 }}
      >
        {saving ? 'SAVING…' : primaryLabel}
      </button>
      {helper && <span style={{ fontSize: 11, lineHeight: 1.5, color: '#6B6B6B', textAlign }}>{helper}</span>}
      {onSkip && (
        <button type="button" onClick={onSkip} data-testid="onboard-skip" className="pk-btn pk-text-on-black" style={{ alignSelf: desk ? 'flex-start' : 'center', height: 36, padding: desk ? 0 : '0 18px', fontSize: 10, fontWeight: 800, letterSpacing: '.16em', color: '#8A8A8A' }}>{skipLabel}</button>
      )}
    </div>
  );
}

/**
 * The sticky top bar of screens 2–5: a 40px circle back, `STEP n OF 4 · TITLE`, a meta on the
 * right (cyan when it counts something), and four 4px segments filled up to the current step.
 */
export function StepTopBar({ step, total = 4, title, meta, metaOn = false, onBack, desk = false }) {
  return (
    <div data-testid="onboard-topbar" style={{ position: desk ? 'static' : 'sticky', top: 0, zIndex: 20, display: 'flex', flexDirection: 'column', gap: 12, padding: desk ? '0 0 10px' : '12px 0 10px', paddingTop: desk ? 0 : 'calc(env(safe-area-inset-top, 0px) + 12px)', background: '#000' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="button" onClick={onBack} aria-label="Back" data-testid="onboard-back" className="pk-btn pk-secondary-on-black" style={{ flexShrink: 0, width: 40, height: 40, padding: 0, borderRadius: '50%' }}>{Icon.back}</button>
        <span style={{ flex: 1, minWidth: 0, fontSize: 10, fontWeight: 800, letterSpacing: '.16em' }}>STEP {step} OF {total} <span style={{ color: '#6B6B6B' }}>· {title}</span></span>
        {meta && <span data-testid="onboard-meta" style={{ flexShrink: 0, fontSize: 10, fontWeight: 800, letterSpacing: '.16em', color: metaOn ? CYAN : '#6B6B6B' }}>{meta}</span>}
      </div>
      <div style={{ display: 'flex', gap: 4 }} aria-hidden="true">
        {Array.from({ length: total }, (_, i) => <span key={i} style={{ flex: 1, height: 4, borderRadius: 999, background: i < step ? '#fff' : '#2A2A2A', transition: 'background-color .2s' }} />)}
      </div>
    </div>
  );
}

/** The moving iridescent ring around a circle or a card: pair with `border: 3px solid transparent` (the ring lives in the border box; `fill` is what sits inside). */
export const irisRing = (fill = '#000') => ({ background: `linear-gradient(${fill},${fill}) padding-box, ${RING} border-box` });

/** A 22px square-ish checkbox (radius 7): black with a #3A3A3A ring, white with a black tick when on. */
export function CheckBox({ on }) {
  return (
    <span aria-hidden="true" style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', background: on ? '#fff' : '#000', boxShadow: 'inset 0 0 0 1px #3A3A3A', color: '#000', transition: 'background-color .2s' }}>
      {on && Icon.tick}
    </span>
  );
}

/** A 24px cyan disc with a black tick — the "picked" mark on a face. */
export function CheckDisc({ size = 24, style }) {
  return <span aria-hidden="true" style={{ width: size, height: size, borderRadius: '50%', background: CYAN, color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}>{Icon.tick}</span>;
}

/** Initials in a 40px circle: #1F1F1F / #8A8A8A, cyan on black when selected. */
export function InitialsDisc({ initials, on, size = 40 }) {
  return <span aria-hidden="true" style={{ flexShrink: 0, width: size, height: size, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: on ? CYAN : '#1F1F1F', color: on ? '#000' : '#8A8A8A', fontSize: 11, fontWeight: 800, letterSpacing: '.08em', transition: 'background-color .2s' }}>{initials}</span>;
}

export const Icon = {
  back: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>,
  next: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>,
  close: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" /></svg>,
  plus: (s = 16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>,
  tick: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="square" aria-hidden="true"><path d="M4 12l5 5L20 6" /></svg>,
  x: <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="square" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" /></svg>,
  // 16px square-cap glyphs in #8A8A8A for the YOU card's handle pills.
  ig: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true" style={{ flexShrink: 0 }}><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><path d="M17.5 6.5h.01" /></svg>,
  tt: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true" style={{ flexShrink: 0 }}><path d="M14 4v11a4 4 0 1 1-4-4" /><path d="M14 4c0 3 2.5 5 5 5" /></svg>,
  lock: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><rect x="5" y="11" width="14" height="10" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>,
};
